<template>
  <div v-if="show" class="update-notification-wrapper">
    <div class="update-notification-content">
      <span>Aplicación actualizada correctamente ✓</span>
    </div>
  </div>
</template>

<script>
import { getUpdateManager } from "@/utils/updateManager";

export default {
  name: "UpdateNotification",
  data() {
    return {
      show: false,
      registration: null,
      refreshing: false,
      updateManager: null,
      notificationTimeout: null,
      updatePending: false,
      updateType: null,
    };
  },
  watch: {
    // Escuchar cambios de ruta para aplicar actualizaciones pendientes cuando sea seguro
    $route() {
      if (this.updatePending && !this.updateManager.isBusy()) {
        console.log(
          "[UpdateNotification] Navegación detectada, aplicando actualización diferida"
        );
        this.updatePending = false;
        this.showNotificationAndUpdate();
      }
    },
  },
  created() {
    // Escuchar el evento personalizado desde el service worker (legacy)
    document.addEventListener("swUpdated", this.onSWUpdated, {
      once: false,
    });

    // Recargar la página cuando el controlador cambie
    if (navigator.serviceWorker) {
      navigator.serviceWorker.addEventListener("controllerchange", () => {
        if (this.refreshing) return;

        // Prevent infinite refresh loop
        const lastReload = sessionStorage.getItem("sw_refresh_ts");
        if (lastReload && Date.now() - lastReload < 10000) {
          console.warn("Preventing infinite reload loop for SW update");
          return;
        }

        this.refreshing = true;
        sessionStorage.setItem("sw_refresh_ts", Date.now());
        window.location.reload();
      });
    }

    // Inicializar el UpdateManager con polling automático
    this.initUpdateManager();
  },
  methods: {
    initUpdateManager() {
      try {
        this.updateManager = getUpdateManager({
          // Polling cada 60 segundos por defecto
          pollInterval: 60000,

          // Callback cuando se detecta una actualización
          onUpdate: (event) => {
            console.log(
              "[UpdateNotification] Actualización detectada",
              event.type
            );
            this.handleUpdate(event);
          },

          // Callback cuando termina la verificación
          onCheckComplete: (result) => {
            if (!result.hasUpdate) {
              // Silenciosamente sin actualización
            }
          },
        });
      } catch (error) {
        console.error("Error inicializando UpdateManager:", error);
      }
    },

    /**
     * Manejador de actualizaciones detectadas
     */
    handleUpdate(event) {
      this.registration = event.detail;
      this.updateType = event.type;

      // Si el usuario está en medio de una tarea importante, postergar
      if (this.updateManager.isBusy()) {
        console.log(
          "[UpdateNotification] Usuario ocupado (ruta crítica), postergando actualización"
        );
        this.updatePending = true;
        return;
      }

      this.showNotificationAndUpdate();
    },

    /**
     * Evento legacy para mantener compatibilidad
     */
    onSWUpdated(event) {
      this.handleUpdate(event);
    },

    /**
     * Muestra una notificación y aplica la actualización
     */
    showNotificationAndUpdate() {
      // Si ya se está refrescando, no hacer nada
      if (this.refreshing) return;

      // Mostrar toast de notificación
      this.show = true;

      // Limpiar timeout anterior si existe
      if (this.notificationTimeout) {
        clearTimeout(this.notificationTimeout);
      }

      console.log(
        `[UpdateNotification] Aplicando actualización de tipo: ${this.updateType}`
      );

      // CASO 1: Actualización vía Service Worker
      if (this.registration && this.registration.waiting) {
        try {
          this.registration.waiting.postMessage({ type: "SKIP_WAITING" });
        } catch (error) {
          console.error("Error al enviar SKIP_WAITING:", error);
        }
      }
      // CASO 2: Desajuste de versión (version.json) o SW no responde pero hay cambios
      else if (
        this.updateType === "version-mismatch" ||
        this.updateType === "manifest-change"
      ) {
        // Guardar el timestamp en localStorage antes de recargar
        if (this.registration && this.registration.timestamp) {
          localStorage.setItem(
            "app_version_timestamp",
            this.registration.timestamp
          );
        }
        // Limpiar caches para cargar los nuevos assets
        if (window.caches) {
          try {
            caches.keys().then((names) => {
              Promise.all(names.map((name) => caches.delete(name)));
            });
          } catch (e) {
            console.error("Error limpiando cache en version-mismatch:", e);
          }
        }
        // Forzar recarga después de un pequeño delay para que el usuario vea el mensaje
        setTimeout(() => {
          if (!this.refreshing) {
            this.refreshing = true;
            window.location.reload();
          }
        }, 2000);
      }

      // Ocultar la notificación después de 3 segundos
      this.notificationTimeout = setTimeout(() => {
        this.show = false;
      }, 3000);
    },
  },
  beforeDestroy() {
    // Limpiar eventos y timers
    document.removeEventListener("swUpdated", this.onSWUpdated);
    if (this.notificationTimeout) {
      clearTimeout(this.notificationTimeout);
    }
  },
};
</script>

<style lang="scss" scoped>
@import "~@/assets/scss/variable";

.update-notification-wrapper {
  position: fixed;
  bottom: 160px;
  right: 20px;
  background-color: var(--modal-bg);
  color: var(--text);
  padding: 16px;
  border-radius: 4px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
  z-index: 9999;
  animation: slideIn 0.3s ease-out;

  .update-notification-content {
    display: flex;
    align-items: center;
    gap: 12px;

    span {
      font-size: 14px;
    }

    .update-btn {
      background-color: var(--btn-bg-primary);
      color: var(--btn-text-primary);
      border: none;
      padding: 6px 12px;
      border-radius: 4px;
      cursor: pointer;
      font-weight: 500;
      transition: background-color 0.2s;

      &:hover {
        background-color: var(--btn-bg-primary);
        color: var(--btn-text-primary);
        transform: scale(1.1);
      }
    }
  }
}

@keyframes slideIn {
  from {
    transform: translateY(100%);
    opacity: 0;
  }

  to {
    transform: translateY(0);
    opacity: 1;
  }
}
</style>
